import { useAtom, useAtomValue } from "jotai";
import { lazy, Suspense, useState } from "react";
import { useMatch } from "react-router-dom";
import { useHotkeys } from "@/hooks/use-hotkeys";
import { cn } from "@/lib/utils";
import { assistantDockedAtom, assistantPanelOpenAtom } from "./store";

const AssistantPanel = lazy(() =>
	import("./assistant-panel").then((module) => ({ default: module.AssistantPanel })),
);

/** Floating sheet above the page, or docked beside it; always a full-screen sheet below `lg`. */
export const assistantSurfaceClassName = (docked: boolean) =>
	cn(
		"flex flex-col overflow-hidden rounded-[10px] border bg-card",
		docked
			? "my-2 mr-2 w-[400px] shrink-0"
			: "fixed right-3 bottom-3 z-40 h-[min(54rem,calc(100dvh-1.5rem))] w-[440px] shadow-xl shadow-black/10 dark:shadow-black/40",
		"max-lg:fixed max-lg:inset-0 max-lg:z-40 max-lg:m-0 max-lg:h-dvh max-lg:w-full max-lg:rounded-none max-lg:border-0",
	);

/**
 * Always mounted in the shell: owns ⌘J and loads the panel chunk on first open. The full
 * assistant page replaces the overlay while it's open without touching the persisted state.
 */
export function AssistantPanelHost() {
	const [open, setOpen] = useAtom(assistantPanelOpenAtom);
	const docked = useAtomValue(assistantDockedAtom);
	const onProjectAssistantPage = !!useMatch("/p/:projectId/assistant");
	const onWorkspaceAssistantPage = !!useMatch("/assistant");
	const onAssistantPage = onProjectAssistantPage || onWorkspaceAssistantPage;
	const [loaded, setLoaded] = useState(open);
	if (open && !loaded) setLoaded(true);
	useHotkeys([
		{
			keys: "mod+j",
			allowInInputs: true,
			enabled: !onAssistantPage,
			handler: () => setOpen((v) => !v),
		},
	]);
	if (!loaded || onAssistantPage) return null;
	return (
		<Suspense fallback={open ? <aside className={assistantSurfaceClassName(docked)} /> : null}>
			<AssistantPanel open={open} />
		</Suspense>
	);
}
